import { Box, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useState } from 'react';
import { TRACE_TYPES_WITH_EXCEPTIONS, TraceType, getTraceTypeLabel } from '../../common/enums/TraceType';
import { useFormItem } from '../../common/hooks/use-form-item/index';
import { InputType } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { ProjectExceptionType, getExceptionTypeLabel } from '../../project-exceptions/enums/ProjectExceptionType';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';

type Props = {
  submit: { label: string; action: (term: string, description: string | null, type: ProjectExceptionType, traceType: TraceType) => Promise<void> };
  back: { label: string; action: () => void };
};

const ExceptionTypeOption = (props: { type: ProjectExceptionType }) => {
  return (
    <Box>
      <Typography variant="body1">{getExceptionTypeLabel(props.type)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {props.type === ProjectExceptionType.BLACKLIST ? t('createExceptionForm.type.helpBlacklist') : t('createExceptionForm.type.helpWhitelist')}
      </Typography>
    </Box>
  );
};

export const ExceptionForm = ({ submit, back }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [traceType, traceTypeComponent, traceTypeValid] = useFormItem<TraceType | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createExceptionForm.traceType.input'),
    value: null,
    validator: required(),
    inputOptions: {
      autoFocus: true,
      disabled: loading,
      focusedLabel: t('createExceptionForm.traceType.focused'),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: false,
      options: TRACE_TYPES_WITH_EXCEPTIONS.map((l) => ({ value: l, label: getTraceTypeLabel(l) })),
    },
  });

  const [type, typeComponent, typeValid] = useFormItem<ProjectExceptionType | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('createExceptionForm.type.input'),
    value: null,
    validator: required(),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createExceptionForm.type.focused'),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: false,
      options: [ProjectExceptionType.BLACKLIST, ProjectExceptionType.WHITELIST].map((e) => ({
        value: e,
        label: getExceptionTypeLabel(e),
        element: <ExceptionTypeOption type={e} />,
        nativeOptionHover: true,
      })),
    },
  });

  const [term, termComponent, termValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('createExceptionForm.term.input'),
    value: '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createExceptionForm.term.focused'),
      showRequiredIndicator: true,
      help: t('createExceptionForm.term.help'),
    },
    textOptions: { trimOnBlur: true },
  });

  const [description, descriptionComponent, descriptionValid] = useFormItem<string | null>({
    type: InputType.TYPE_TEXT,
    label: t('createExceptionForm.description.input'),
    value: '',
    validator: maxLength(1000),
    inputOptions: {
      disabled: loading,
      focusedLabel: t('createExceptionForm.description.focused'),
    },
    textOptions: { trimOnBlur: true },
  });

  const formValid = !!term && !!type && !!traceType;
  const canSubmit = !loading && traceTypeValid && typeValid && termValid && descriptionValid;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!formValid) return;
    try {
      setLoading(true);
      await submit.action(term, description, type, traceType);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={1.5} component="form" onSubmit={onSubmit}>
      {traceTypeComponent}
      {typeComponent}
      {termComponent}
      {descriptionComponent}
      <ButtonBox>
        <TertiaryButton onClick={back.action} disabled={loading}>
          {back.label}
        </TertiaryButton>
        <PrimaryButton disabled={!canSubmit} type="submit">
          {submit.label}
        </PrimaryButton>
      </ButtonBox>
    </Stack>
  );
};
